//******************************************************************************
//  SAP Explore · Document Discovery Agent – models & reference data
//******************************************************************************

export type DocType = 'flow' | 'signavio' | 'bpd' | 'cdd' | 'kt' | 'sdr' | 'other';
export type InputDocType = Exclude<DocType, 'other'>;
export type Landscape = 'asis' | 'tobe';
export type LandscapeScope = Landscape | 'both';
export type SourceKey = string;
export type FileExt = 'docx' | 'xlsx' | 'pdf' | 'pptx' | 'bpmn' | 'svg';
export type Decision = 'inc' | 'exc' | 'pend';
export type ConfidenceClass = 'h' | 'md' | 'l';
export type View = 'search' | 'history' | 'packs' | 'process' | 'taxonomy' | 'log' | 'help' | 'settings';
export type SortKey = 'conf' | 'name' | 'date';

export interface L2Process { id: string; name: string; }

export interface DocTypeInfo {
    short: string;
    long: string;
    color: string;
    asis: string | null;
    tobe: string | null;
}

export interface SourceInfo { name: string; n: number | null; }

export interface FoundDocument {
    id: string;
    t: DocType;
    land: Landscape | 'unknown';
    ext: FileExt;
    name: string;
    src: SourceKey;
    path: string;
    l2: string[];
    codes: string[];
    conf: number;
    why: string;
    mod: string;
    by: string;
    /** Signal flags (1 = satisfied) aligned with SIGNALS */
    sig: number[];
    flag?: string;
    /** The agent's own call: relevant, needs your call, or excluded (with its reason). Seeds the review decisions. */
    agent?: 'inc' | 'call' | 'exc';
    agentReason?: string;
    /** Duplicates the agent removed (this is the copy kept) */
    copies?: { name: string; path: string }[];
    /** Other candidate versions when none is provably the latest */
    alternates?: { name: string; path: string }[];
    /** Version found inside the document, e.g. "1.5" */
    version?: string;
    versionUnclear?: boolean;
    /** Whether the document content is readable (has text content) */
    readable?: boolean;
    /** Summary or excerpt from the document content */
    summary?: string;
    /** Evidence lines the agent used to pick the document */
    evidence?: string[];
    /** Where the match came from, e.g. "metadata" */
    match_source?: string;
    signavioFlowId?: string;
}

export interface SearchSummary {
    filesScanned: number;
    candidates: number;
    found: number;
    relevant: number;
    needsCall: number;
    excluded: number;
}

export interface SearchStep {
    key: string;
    title: string;
    detail: string;
    durationSec: number;
    status: 'done';
}

export interface SearchEnvelope {
    runId: string;
    summary: SearchSummary;
    steps: SearchStep[];
    documents: FoundDocument[];
    missing: MissingDocument[];
}

export interface MissingDocument {
    id?: string;
    attachment?: {
        id: string;
        name: string;
        contentType: string;
        size: number;
        uploadedAt: string;
        status: 'stored';
    } | null;
    type: InputDocType;
    land: Landscape;
    l2: string;
    l3?: string;
    title: string;
    loc?: string;
    owner?: string;
    date?: string;
    marker?: string;
    why?: string;
    key: string;
}

export interface SearchScope {
    l2: Set<string>;
    land: LandscapeScope;
    types: Set<InputDocType>;
    src: Set<SourceKey>;
}

export interface ReviewFilters {
    conf: 'all' | ConfidenceClass;
    l2: string;
    src: 'all' | SourceKey;
    land: 'all' | Landscape;
    q: string;
}

export interface PublishedPack { id: string; at: string; n: number; }

export interface TraceStep { title: string; detail: string; log: string[]; duration: number; }

export interface NavItem { view: View; label: string; icon: string; }

//******************************************************************************
//  Reference data. Filled at startup from GET api/document-finder/bootstrap,
//  /process-map and /taxonomy (DocumentFinderService.loadReference). The containers are
//  mutated in place so components that import them see the API values.
//******************************************************************************
export const L2_PROCESSES: L2Process[] = [];

/**
 * Document type labels. `short` and `color` are display settings. `long` comes from
 * bootstrap.types and `asis`/`tobe` (availability per landscape) from the taxonomy;
 * both are filled by applyDocTypeReference() once the API responds.
 */
export const DOC_TYPES: Record<DocType, DocTypeInfo> = {
    flow: { short: 'Process flow', long: '', color: 'var(--t-flow)', asis: null, tobe: null },
    signavio: { short: 'Signavio', long: 'Signavio process flows', color: '#9B3D72', asis: null, tobe: 'Signavio' },
    bpd: { short: 'BPD', long: 'Business Process Design (BPD)', color: 'var(--t-bpd)', asis: null, tobe: null },
    cdd: { short: 'CDD', long: '', color: 'var(--t-cdd)', asis: null, tobe: null },
    kt: { short: 'KT', long: '', color: 'var(--t-kt)', asis: null, tobe: null },
    sdr: { short: 'SDR', long: '', color: 'var(--t-sdr)', asis: null, tobe: null },
    other: { short: 'Unclassified', long: '', color: 'var(--t-other)', asis: null, tobe: null }
};

/** Taxonomy cell → availability text, or null when the type does not exist in that landscape. */
function availability(value: string | undefined): string | null {
    const v = (value ?? '').trim();
    return !v || ['—', '-', 'no', 'n/a', 'none'].includes(v.toLowerCase()) ? null : v;
}

/**
 * Applies API reference data to DOC_TYPES.
 * @param types bootstrap `types` map (key → long label)
 * @param rows taxonomy rows (document label, as-is and to-be availability)
 */
export function applyDocTypeReference(types: Record<string, string>, rows: { document: string; asis: string; tobe: string }[]): void {
    (Object.keys(DOC_TYPES) as DocType[]).forEach(k => {
        if (k === 'signavio') { return; }
        DOC_TYPES[k].long = types[k]?.trim() || (k === 'bpd' ? 'Business Process Design (BPD)' : DOC_TYPES[k].short);
        const row = rows.find(r => {
            const doc = (r.document ?? '').trim().toLowerCase();
            return doc === k || doc === DOC_TYPES[k].short.toLowerCase()
                || (!!types[k] && doc === types[k].trim().toLowerCase())
                || doc.split('/').some(part => part.trim() === k);
        });
        DOC_TYPES[k].asis = availability(row?.asis);
        DOC_TYPES[k].tobe = availability(row?.tobe);
    });
}

export const TYPE_ORDER: DocType[] = ['flow', 'signavio', 'bpd', 'cdd', 'kt', 'sdr', 'other'];
export const INPUT_TYPES: InputDocType[] = ['flow', 'signavio', 'bpd', 'cdd', 'kt', 'sdr'];
export const LANDSCAPES: Landscape[] = ['asis', 'tobe'];

/** Sources from the API, keyed by source key (e.g. 'az'). */
export const SOURCES: Record<SourceKey, SourceInfo> = {};
/** Keys of the API sources, in API order. */
export const SOURCE_KEYS: SourceKey[] = [];
/** Key of the client-side "Connect folder" source (Azure Storage via SAS). */
export const CONNECTED_FOLDER_KEY = 'folder';

/** Exclusion reasons accepted by the API. */
export const REASONS: string[] = [];

/** Display name of a source, falling back to its key. */
export function sourceName(key: SourceKey): string {
    return SOURCES[key]?.name ?? key;
}

/** Labels for the five relevance signals in `FoundDocument.sig`. Overridden by bootstrap.signals when provided. */
export const SIGNALS: [string, string][] = [
    ['Mentions an in-scope process or scope item', 'Process codes found in the document metadata or content'],
    ['Template structure matches its document type', 'Document type classified from the index'],
    ['Filename carries the process code', 'Checked last; filenames are inconsistent'],
    ['Sits in the expected library or folder', 'Source system and path from the index'],
    ['Latest version, not superseded', 'Compared against near-duplicates and modified dates']
];

export const STEPS: string[] = ['Ask', 'Retrieve', 'Review and choose', 'Feedback', 'Approve', 'PBLs', 'Deck'];

/** Suggested questions. Overridden by bootstrap.suggestions when provided. */
export const SUGGESTIONS: string[] = [
    'Create an Explore Design deck for HCVC09',
    'Generate a Lift & Fit deck for Production Execution',
    'Build an Explore Design deck for Indirect Material Procurement (MM02)',
];

/** Initial search text. Overridden by bootstrap.default_query when provided. */
export const DEFAULT_QUERY = 'Build an Explore Design deck for Production Execution (HCVC09).';

/** Steps of the last search, filled from the search API response. */
export const TRACE: TraceStep[] = [];

export interface ProcessMapEntry { code: string; name: string; description: string; }
/** Filled from GET api/document-finder/process-map. */
export const PROCESS_MAP: ProcessMapEntry[] = [];

export interface TaxonomyData {
    rows: { document: string; asis: string; tobe: string; role: string }[];
    acronyms: { term: string; meaning: string }[];
}
/** Filled from GET api/document-finder/taxonomy. */
export const TAXONOMY: TaxonomyData = { rows: [], acronyms: [] };

/** Replaces the contents of an array in place. */
export function replaceAll<T>(target: T[], values: T[]): void {
    target.splice(0, target.length, ...values);
}

export const NAV_GROUPS: { title: string | null; items: NavItem[] }[] = [
    { title: 'DOCUMENT DISCOVERY', items: [
        { view: 'search', label: 'New search', icon: 'search' },
        { view: 'history', label: 'Search history', icon: 'hist' },
        { view: 'packs', label: 'Published packs', icon: 'pack' }
    ] },
    { title: 'EXPLORE WORKSPACE', items: [
        { view: 'process', label: 'Process map (L1–L3)', icon: 'map' },
        { view: 'taxonomy', label: 'Document taxonomy', icon: 'tax' }
    ] },
    { title: 'FEEDBACK', items: [
        { view: 'log', label: 'Relevance feedback log', icon: 'fb' }
    ] },
    { title: null, items: [
        { view: 'help', label: 'Help', icon: 'help' },
        { view: 'settings', label: 'Settings', icon: 'set' }
    ] }
];

//******************************************************************************
//  Retrieved documents. Filled from POST api/document-finder/search.
//******************************************************************************
export const DOCUMENTS: FoundDocument[] = [];

//******************************************************************************
//  Helpers
//******************************************************************************
export function confidenceClass(conf: number): ConfidenceClass {
    return conf >= 80 ? 'h' : conf >= 60 ? 'md' : 'l';
}

export function confidenceLabel(conf: number): string {
    return conf >= 80 ? 'High' : conf >= 60 ? 'Medium' : 'Low';
}

export function documentLandscape(doc: FoundDocument): FoundDocument['land'] {
    const source = (doc.src ?? '').trim().toLowerCase();
    return source === 'dm' || source === 'documentum' ? 'asis' : doc.land;
}

export function landscapeLabel(land: FoundDocument['land'], long: boolean = false): string {
    if (land !== 'asis' && land !== 'tobe') { return 'Landscape unknown'; }
    if (long) {
        return land === 'asis' ? 'As-Is (ECC)' : 'To-Be (S/4HANA)';
    }
    return land === 'asis' ? 'As-Is' : 'To-Be';
}

export function gapKey(type: DocType, land: Landscape, l2: string): string {
    return `${type}|${land}|${l2}`;
}
